import { useState } from 'react';
import { ApplyApi, AuthorizedDrawUserDto } from '@apply/api/src/api/ApplyApi';
import { ApiResponse } from '@apply/api/src/api/bfiApi';
import { GenericAlertPopup } from '@apply/components';
import { applicationInsights } from '../../ApplicationInsights';

export interface UseGCUserModalReturn {
    isOpen: boolean;
    isLoading: boolean;
    authorizedDrawUser: AuthorizedDrawUserDto | undefined;
    loadAndOpen: (userId: number) => Promise<void>;
    openNew: () => void;
    close: () => void;
    onSubmitAsync: (userToSave: Partial<AuthorizedDrawUserDto>) => Promise<boolean>;
}

/**
 * Custom hook to manage GC User Modal state and operations
 * Handles loading, creating, and updating authorized draw users
 */
export const useGCUserModal = (onRefresh: () => void): UseGCUserModalReturn => {
    const applyApi = new ApplyApi(applicationInsights);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [authorizedDrawUser, setAuthorizedDrawUser] = useState<AuthorizedDrawUserDto | undefined>(undefined);

    // Load an existing user and open the modal
    const loadAndOpen = async (userId: number) => {
        setIsLoading(true);
        try {
            const response: ApiResponse<AuthorizedDrawUserDto> = await applyApi.getDrawAuthorizedUser(userId);
            if (response.statusCode === 200 && response.result) {
                setAuthorizedDrawUser(response.result);
                setIsOpen(true);
            } else {
                const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to load user';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            setTimeout(() => {
                GenericAlertPopup({
                    title: 'Error',
                    children: 'Failed to load user data. Please try again.',
                    width: '350px',
                });
            }, 100);
        } finally {
            setIsLoading(false);
        }
    };

    // Open modal for creating a new user
    const openNew = () => {
        setAuthorizedDrawUser(undefined);
        setIsOpen(true);
    };

    // Close modal and reset state
    const close = () => {
        setIsOpen(false);
        setAuthorizedDrawUser(undefined);
    };

    // Submit handler for both creating and updating users
    const onSubmitAsync = async (userToSave: Partial<AuthorizedDrawUserDto>): Promise<boolean> => {
        try {
            if (authorizedDrawUser) {
                // Update existing user
                const response = await applyApi.updateDrawAuthorizedUser({
                    id: authorizedDrawUser.id,
                    displayRole: userToSave.displayRole,
                    displayFirstName: userToSave.displayFirstName,
                    displayLastName: userToSave.displayLastName,
                    displayPhone: userToSave.displayPhone,
                    isApprovalRequired: userToSave.isApprovalRequired
                });

                if (response.statusCode === 200) {
                    GenericAlertPopup({
                        title: 'Success',
                        children: 'User updated successfully!',
                        width: '350px'
                    });
                    // Delay refresh to allow success alert animation to complete
                    setTimeout(() => {
                        onRefresh();
                    }, 500);
                    return true;
                } else {
                    const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to update user';
                    throw new Error(errorMsg);
                }
            } else {
                // Invite new user
                const response = await applyApi.inviteDrawAuthorizedUser({
                    email: userToSave.email!,
                    displayRole: userToSave.displayRole!,
                    displayFirstName: userToSave.displayFirstName!,
                    displayLastName: userToSave.displayLastName!,
                    displayPhone: userToSave.displayPhone!,
                    isApprovalRequired: userToSave.isApprovalRequired!
                });

                if (response.statusCode === 200) {
                    GenericAlertPopup({
                        title: 'Success',
                        children: 'User invitation sent successfully!',
                        width: '350px'
                    });
                    // Delay refresh to allow success alert animation to complete
                    setTimeout(() => {
                        onRefresh();
                    }, 500);
                    return true;
                } else {
                    const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to send invitation';
                    throw new Error(errorMsg);
                }
            }
        } catch (error) {
            console.error('Failed to save user:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save user. Please try again.';
            GenericAlertPopup({
                title: 'Error',
                children: errorMessage,
                width: '350px',
            });
            return false;
        }
    };

    return {
        isOpen,
        isLoading,
        authorizedDrawUser,
        loadAndOpen,
        openNew,
        close,
        onSubmitAsync,
    };
};
